import React, { useCallback, useMemo, useState } from 'react';
import type { AxiosProgressEvent } from 'axios';
import {
    Alert,
    AutoGrid,
    Button,
    DialogV2,
    FileUploadDropzone,
    ListItemText,
    SelectField,
} from 'c-components';
import { useCommonTranslation } from 'c-translation';

import type { LinearProgressProps, SelectChangeEvent, TypographyProps } from '@mui/material';
import {
    Box,
    CircularProgress,
    FormLabel,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useAPIClientRequest } from 'c-hooks';
import { apiClient } from 'c-data';
import to from 'await-to-js';
import { NetworkRequestState } from 'c-data-layer';
import { CheckCircle, Error } from '@mui/icons-material';
import { formatBytes } from 'c-lib';
import type { Campaign } from 'c-sdk';
import useInsertionOrdersMissing from 'c-main/Components/Schedule/Dsp/useInsertionOrdersMissing';

type BooleanState = {
    value: boolean;
    toggle: () => void;
    setTrue: () => void;
    setFalse: () => void;
};
interface PcaDialogProps {
    id: string | number;
    pcaState: BooleanState;
    campaign: any;
}

const primaryTypography: TypographyProps<any> = {
    component: 'div',
    sx: { wordBreak: 'break-all' },
};
const templateCsvHeader = `"id","name","reference","buyer","agency","client","scheduled","frame_id","inside_campaign_settings_date","playout_start_date","playout_start_time","playout_start_datetime","playout_end_date","playout_end_time","playout_end_datetime","playout_day_of_week","creative_id","creative_name","sum_of_play","expected_plays","sum_of_creatives","sum_of_time","sov","slot_duration","expected_time","audience_sov","audience","playout_percentage_plays","playout_percentage_time","expected_audience","audience_received","audience_received_percent","expected_impacts_in_schedule","impacts_received_in_schedule","impacts_received_out_of_schedule","impacts_received_unbooked","expected_plays_in_schedule","plays_received_in_schedule","plays_received_out_of_schedule","plays_received_unbooked","expected_time_in_schedule","time_received_in_schedule","time_received_out_of_schedule","time_received_unbooked","day","hour","display_id","issue_id","downtime","plays_with_verification","time_with_verification","impacts_with_verification","energy_uses_kwh","carbon_emission_kgs_co2e","display_sov","display_slots","display_slot_duration","display_town","display_tv_region","display_conurbation","display_type","display_resolution","display_filetypes","display_env","display_surface_area","display_lat","display_long","display_format","display_line_item_id","display_line_item_name","display_owner","display_owner_id","display_screen_name_summary","playout_start_timestamp","playout_start_hour","playout_end_timestamp","playout_end_hour","audience_version","geopath_audience","geopath_sov","environment","campaign_id"`;

const templateCsvExampleRow = `"11707","Omoda Q4","K61137","Kinetic - Wavemaker","Omoda","OMODA","IN","2000180314","1","2025-10-20","00:00:00","2025-10-20 00:00:00.000","2025-10-20","00:59:59","2025-10-20 00:59:59.000","Monday","1934772","UniLIVE_11707_Global_Peckham_480x720_Jaecoo7_AW.jpg (0000091245 - 155786)","39.0","30.0","2.0","390.0","8.333333335","10.0","300.0","10.0","0.25716","129.99999","129.99999","214.30642","278.59834","129.99999","214.30642","278.59834","0.0","0.0","30.0","39.0","0.0","0.0","300.0","390.0","0.0","0.0","0","0","38167","","0","39.0","390.0","278.59834","0.0","0.0","16.6666666667","6.0","10.0","Peckham","London","Greater London","Display","480x720","jpeg","Roadside","34.56","51.47389182","-0.06758756","DM6","6d227b62-3e61-4d65-8b88-3f41855549a4","Peckham High Street","Global","135","Peckham High Street","1760918400","0","1760921999","0","fallback-version-69",,,"sandbox","11707"`;

function formatDateString(dateString: string) {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    const timeZoneCode = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(date)
        .find(part => part.type === 'timeZoneName').value;

    return `${day}/${month}/${year} ${hours}:${minutes} ${timeZoneCode}`;
}

const memoryOptions = [
    { label: 'Auto', value: 'auto' },
    { label: '8 GB', value: '8000' },
    { label: '16 GB', value: '16000' },
    { label: '24 GB', value: '24000' },
    { label: '30 GB', value: '30000' },
    { label: '32 GB', value: '32000' },
    { label: '60 GB', value: '60000' },
    { label: '64 GB', value: '64000' },
    { label: '80 GB', value: '80000' },
    { label: '96 GB', value: '96000' },
    { label: '112 GB', value: '112000' },
    { label: '120 GB', value: '120000' },
];

export const PcaDialog: React.FC<PcaDialogProps> = ({ id, pcaState, campaign }) => {
    const t = useCommonTranslation();
    const insertionOrdersMissing = useInsertionOrdersMissing(campaign);
    const [environment, setEnvironment] = useState<string>();
    const [files, setFiles] = useState<File[]>();
    const [progress, setProgress] = useState<AxiosProgressEvent | null>(null);
    const [option, setOption] = useState<number | null | string>(
        campaign.fargate_cad_memory_override,
    );
    const progressPercentage = useMemo(
        () =>
            progress?.total != null && progress?.loaded != null
                ? (progress.loaded / progress.total) * 100
                : 0,
        [progress],
    );
    const {
        start,
        error,
        requestState,
        reset: resetNetworkState,
    } = useAPIClientRequest(apiClient.Entities.Campaign.pcaImport);
    const {
        start: downloadStart,
        error: downloadError,
        requestState: downloadState,
        reset: downloadReset,
    } = useAPIClientRequest(apiClient.Entities.Campaign.pcaExport);
    const {
        start: runStart,
        requestState: runRequestState,
        error: runError,
        reset: runReset,
    } = useAPIClientRequest(apiClient.Reports.pcaRunReport);
    const {
        start: deleteStart,
        requestState: delRequestState,
        error: delError,
        reset: delReset,
    } = useAPIClientRequest(apiClient.Reports.pcaDeleteReport);
    const {
        start: startPublish,
        requestState: publishState,
        error: publicError,
        reset: publishReset,
    } = useAPIClientRequest(apiClient.Reports.publishReport);
    const { start: campaignStart } = useAPIClientRequest(apiClient.Entities.Campaign.update);

    const handleFormSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(async () => {
        downloadReset();
        resetNetworkState();
        publishReset();
        delReset();
        runReset();
        setProgress(null);
        const formData = new FormData();
        formData.append('environment', environment as string);
        const [err] = await to(downloadStart(Number(id), formData, { responseType: 'blob' }));
        setTimeout(() => downloadReset(), 3000);
    }, [
        delReset,
        downloadReset,
        downloadStart,
        environment,
        id,
        publishReset,
        resetNetworkState,
        runReset,
    ]);
    const progressColor = useMemo<LinearProgressProps['color']>(() => {
        if (requestState === NetworkRequestState.Error) return 'error';
        if (progressPercentage >= 100) return 'success';

        return 'primary';
    }, [requestState, progressPercentage]);

    const onFilesAdded = useCallback(
        async (newFiles: File[]) => {
            setFiles(newFiles);
            downloadReset();
            resetNetworkState();
            publishReset();
            delReset();
            runReset();
            setProgress(null);
            const formData = new FormData();
            formData.append('environment', environment as string);
            if (newFiles.length !== 0) {
                formData.append('file', newFiles[0]);
            }
            const [err, success] = await to(
                start(Number(id), { formData, onProgress: setProgress }),
            );
            setTimeout(() => resetNetworkState(), 3000);
        },
        [
            downloadReset,
            resetNetworkState,
            publishReset,
            delReset,
            runReset,
            environment,
            start,
            id,
        ],
    );

    const envHandler = useCallback(
        (event: SelectChangeEvent<unknown>) => {
            setTimeout(() => {
                resetNetworkState();
                downloadReset();
                runReset();
                delReset();
                publishReset();
            }, 3000);
            const selectedValue = event.target.value as string;
            setEnvironment(selectedValue);
            setFiles(null);
        },
        [delReset, downloadReset, publishReset, resetNetworkState, runReset],
    );

    const generateCsvTemplate = useCallback((csv: string) => {
        const blob = new Blob([`${csv}\n`], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute('download', 'template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);
    const handleGenerateTemplateClick = () => {
        generateCsvTemplate(`${templateCsvHeader}\n${templateCsvExampleRow}`);
    };
    const textProps = useMemo<TypographyProps<any, any>>(() => {
        if (requestState === NetworkRequestState.Success)
            return { color: 'success.main', ...primaryTypography };
        if (requestState === NetworkRequestState.Error)
            return { color: 'error.main', ...primaryTypography };
        return { component: 'div', ...primaryTypography };
    }, [requestState]);
    const onClose = () => {
        pcaState.setFalse();
        setFiles(null);
        setEnvironment(null);
        resetNetworkState();
        downloadReset();
        runReset();
        delReset();
        publishReset();
        setProgress(null);
    };

    const handleRun = useCallback(() => {
        runStart({ id: Number(id), consumerType: 'full', environment });
        setTimeout(() => runReset(), 3000);
    }, [environment, id, runReset, runStart]);
    const handleDelete = useCallback(() => {
        deleteStart({ id: Number(id), environment });
        setTimeout(() => delReset(), 3000);
    }, [delReset, deleteStart, environment, id]);
    const handlePublish = useCallback(() => {
        startPublish({ id });
        setTimeout(() => publishReset(), 3000);
    }, [startPublish, id, publishReset]);
    const isAnyInProgress = useMemo(
        () =>
            [downloadState, requestState, runRequestState, delRequestState, publishState].includes(
                NetworkRequestState.InProgress,
            ),
        [delRequestState, downloadState, publishState, requestState, runRequestState],
    );

    return (
        <>
            <DialogV2
                onClose={onClose}
                open={pcaState.value === true}
                title="Modules.Main.Campaigns.Overview.Cad.title"
                maxWidth="md"
            >
                <Box>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ width: '50%' }}>
                            <Stack>
                                <FormLabel>
                                    {t('Modules.Main.Campaigns.Overview.Cad.environment')}
                                </FormLabel>
                                <SelectField
                                    value={environment}
                                    onChange={envHandler}
                                    disabled={
                                        requestState === NetworkRequestState.InProgress ||
                                        downloadState === NetworkRequestState.InProgress
                                    }
                                    options={[
                                        {
                                            value: 'live',
                                            label: t('Modules.Main.Campaigns.Overview.Cad.client'),
                                        },
                                        {
                                            value: 'sandbox',
                                            label: t(
                                                'Modules.Main.Campaigns.Overview.Cad.internal',
                                            ),
                                        },
                                    ]}
                                />
                                <Stack gap={1}>
                                    <Stack direction="row" gap={1} pt={2}>
                                        <Tooltip
                                            title={
                                                insertionOrdersMissing
                                                    ? t(
                                                          'Modules.Main.Campaigns.Overview.Cad.noInsertionOrders',
                                                      )
                                                    : ''
                                            }
                                        >
                                            <span>
                                                <Button
                                                    disabled={
                                                        !environment ||
                                                        isAnyInProgress ||
                                                        insertionOrdersMissing
                                                    }
                                                    onClick={handleRun}
                                                >
                                                    {t('Modules.Main.Campaigns.Overview.Cad.run')}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Button
                                            disabled={!environment || isAnyInProgress}
                                            onClick={handleFormSubmit}
                                        >
                                            {downloadState === NetworkRequestState.InProgress ? (
                                                <CircularProgress />
                                            ) : (
                                                t('Modules.Main.Campaigns.Overview.Cad.Download')
                                            )}
                                        </Button>
                                        <Button
                                            disabled={!environment || isAnyInProgress}
                                            onClick={handleDelete}
                                        >
                                            {t('Modules.Main.Campaigns.Overview.Cad.delete')}
                                        </Button>
                                        {environment === 'sandbox' && (
                                            <Button
                                                disabled={!environment || isAnyInProgress}
                                                onClick={handlePublish}
                                            >
                                                {t('Modules.Main.Campaigns.Overview.Cad.publish')}
                                            </Button>
                                        )}
                                    </Stack>
                                    <Typography>
                                        {t('Modules.Main.Campaigns.Overview.Cad.lastRunAt')}:{' '}
                                        {!campaign.pca_available[environment]
                                            ? t('Modules.Main.Campaigns.Overview.Cad.never')
                                            : formatDateString(campaign.pca_available[environment])}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>

                        <Box sx={{ width: '50%' }}>
                            <Stack>
                                <FormLabel>
                                    {t('Modules.Main.Campaigns.Overview.Cad.workerRam')}
                                </FormLabel>
                                <SelectField
                                    value={option === null ? 'auto' : String(option)}
                                    onChange={e => {
                                        const selected = e.target.value;
                                        const val = selected === 'auto' ? null : Number(selected);
                                        setOption(val);
                                        campaignStart(Number(id), {
                                            fargate_cad_memory_override: val,
                                        } as Campaign);
                                    }}
                                    options={memoryOptions}
                                />
                            </Stack>
                        </Box>
                    </Stack>

                    {downloadState === NetworkRequestState.Error && (
                        <Box pb={2}>
                            <Alert severity="error" variant="outlined">
                                {String(downloadError)}
                            </Alert>
                        </Box>
                    )}
                    {downloadState === NetworkRequestState.Success && (
                        <Box pb={2}>
                            <Alert severity="success" variant="outlined">
                                {t('Modules.Main.Campaigns.Overview.Cad.downloadSuccess')}
                            </Alert>
                        </Box>
                    )}
                    {runRequestState === NetworkRequestState.Error && (
                        <Box pb={2}>
                            <Alert severity="error" variant="outlined">
                                {String(runError)}
                            </Alert>
                        </Box>
                    )}
                    {runRequestState === NetworkRequestState.Success && (
                        <Box pb={2}>
                            <Alert severity="success" variant="outlined">
                                {t('Modules.Main.Campaigns.Overview.Cad.runSuccess')}
                            </Alert>
                        </Box>
                    )}
                    {delRequestState === NetworkRequestState.Error && (
                        <Box pb={2}>
                            <Alert severity="error" variant="outlined">
                                {String(delError)}
                            </Alert>
                        </Box>
                    )}
                    {delRequestState === NetworkRequestState.Success && (
                        <Box pb={2}>
                            <Alert severity="success" variant="outlined">
                                {t('Modules.Main.Campaigns.Overview.Cad.deleteSuccess')}
                            </Alert>
                        </Box>
                    )}
                    {publishState === NetworkRequestState.Error && (
                        <Box pb={2}>
                            <Alert severity="error" variant="outlined">
                                {String(publicError)}
                            </Alert>
                        </Box>
                    )}
                    {publishState === NetworkRequestState.Success && (
                        <Alert severity="success" variant="outlined">
                            {t('Modules.Main.Campaigns.Overview.Cad.publishSuccess')}
                        </Alert>
                    )}
                    <Stack gap={1}>
                        <FileUploadDropzone
                            disabled={!environment}
                            fileAccept=".csv, .zip"
                            onFilesAdded={onFilesAdded}
                            dropzoneLabel="Modules.Main.Campaigns.Overview.BookingSummary.dropzoneText"
                        />
                        <Box justifyContent="center" display="flex">
                            <Button onClick={handleGenerateTemplateClick}>
                                {t('Modules.Main.Campaigns.Overview.Cad.template')}
                            </Button>
                        </Box>
                    </Stack>
                    {files && (
                        <ListItemText
                            primary={files[0].name}
                            primaryTypographyProps={textProps}
                            secondaryTypographyProps={textProps}
                            secondary={
                                <Stack gap={1} width="100%">
                                    <AutoGrid spacing={1}>
                                        {formatBytes(files[0]?.size)} | {files[0]?.type}
                                    </AutoGrid>
                                    <Box width="100%">
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Box flex={1}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progressPercentage}
                                                    color={progressColor}
                                                />
                                            </Box>
                                            {requestState === NetworkRequestState.InProgress && (
                                                <Typography variant="caption">
                                                    {progressPercentage.toFixed(0)} %
                                                </Typography>
                                            )}
                                            {requestState === NetworkRequestState.Error && (
                                                <Tooltip title={String(error)}>
                                                    <Typography color="error.main">
                                                        <Error />
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                            {requestState === NetworkRequestState.Success && (
                                                <Typography color="success.main">
                                                    <CheckCircle />
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            }
                        />
                    )}
                    {requestState === NetworkRequestState.Error && (
                        <Box pb={2}>
                            <Alert severity="error" variant="outlined">
                                {String(error)}
                            </Alert>
                        </Box>
                    )}
                </Box>
            </DialogV2>
        </>
    );
};
